import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import classNames from "classnames";
import styles from "./index.module.scss";
import CommentGroup from "../../../ADP/CommentGroup";
import PageMarker from "../../../ADP/PageMarker";
import TextArea from "../../../TextArea";
import TextLink from "../../../TextLinks";
import CheckboxGroup from "../../../CheckboxGroup";
import RadioGroup from "../../../RadioGroup";
import { Text } from "../../../Type";
import Tooltip from "../../../Tooltip";
import ToggleAllUnitsButton from "../../../ToggleButton/ToggleAllUnitsButton";
import Modal from "../../../Modal";
import Button from "../../../Button";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import TextGrid from "../../../TextGrid";
import { useToggleAllUnits } from "../../../../hooks/useToggleAllUnits";
import MeasurementInput from "../../../MeasurementInput";
import UploadDocumentsModal from "../../../ADP/UploadDocumentsModal";
import {
  customModificationsOptions,
  reasonOptions,
} from "../../../../utils/labelMap/LabelMap";
import { nonSpecialCharacterOptions } from "../../../../utils/forms/validation";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { useTriggerOnFieldChange } from "../../../../hooks/useTriggerOnFieldChange";

const Section2b = () => {
  const { clientFile, formMode: mode } = useClientFileContext();
  const {
    state: { user, externalUrls },
  } = useStoreContext();
  const [isUploadModalOpen, setUploadModalOpen] = useState(false);
  const isSalesRep = user?.role.name === "Sales Rep";
  const fileStatus = clientFile.status;
  const currDevice = useWatch({ name: "section2b.base-device" });
  const reasonForApplication2b = useWatch({
    name: "section2b.reason-for-application",
  });

  // #region options
  const baseDeviceOptions = [
    {
      label: "Adult Standard Manual Wheelchair",
      value: "asmw",
      tooltip: (
        <Tooltip
          id="asmw-tooltip"
          content="Wheelchair type most suitable for user who is not consistently independently mobile and requires minimal adjustments to accommodate postural and mobility requirements. ADP price includes frame (sizes up to 18&Prime;wide x 18&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Lightweight Standard Manual Wheelchair",
      value: "alsmw",
      tooltip: (
        <Tooltip
          id="alsw-tooltip"
          content="Wheelchair type most suitable for user who is independently mobile and requires some adjustments to accommodate postural and mobility requirements. ADP price includes frame (sizes up to 18&Prime;wide x 18&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Lightweight Performance Manual Wheelchair",
      value: "alpmw",
      tooltip: (
        <Tooltip
          id="alpmw-tooltip"
          content="Wheelchair type offers maximum adjustments and options. Suitable for the very active user who requires altered wheel placement for optimal postural support and mobility. ADP price includes frame (sizes up to 18&Prime;wide x 18&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult High Performance Rigid Manual Wheelchair",
      value: "ahprmw",
      tooltip: (
        <Tooltip
          id="ahprmw-tooltip"
          content="Wheelchair type offers lightweight rigid frame and optimal maneuverability. Suitable for the very active user who requires postural support and altered wheel placement for mobility. ADP price includes frame (sizes up to 18&Prime;wide x 18&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear anti-tippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Adult Manual Dynamic Tilt Wheelchair",
      value: "amdtw",
      tooltip: (
        <Tooltip
          id="amdtw-tooltip"
          content="Wheelchair type offers manual dynamic tilt required by the user who can not maintain an upright posture through the use of seating components alone. ADP price includes dynamic tilt frame (sizes up to 18&Prime; wide x 18&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, brake extensions, 2 point seat belt any closure, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Lightweight Standard Manual Wheelchair",
      value: "plsmw",
      tooltip: (
        <Tooltip
          id="plsmw-tooltip"
          content="Wheelchair type most suitable for user who requires some adjustments to accommodate postural and mobility requirements. ADP price includes frame (sizes up to 16&Prime;wide x 16&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Lightweight Performance Manual Wheelchair",
      value: "plpmw",
      tooltip: (
        <Tooltip
          id="plpmw-tooltip"
          content="Wheelchair type offers maximum adjustments and options. Suitable for the active user who requires altered wheel placement for optimal postural support and mobility. ADP price includes frame (sizes up to 16&Prime;wide x 16&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric High Performance Rigid Manual Wheelchair",
      value: "phprmw",
      tooltip: (
        <Tooltip
          id="phprmw-tooltip"
          content="Wheelchair type offers lightweight rigid frame and optimal maneuverability. Suitable for the very active user who requires postural support and altered wheel placement for mobility. ADP price includes frame (sizes up to 16&Prime;wide x 16&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear anti-tippers, 2 point seat belt any closure, brake extensions, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Manual Dynamic Tilt Wheelchair",
      value: "pmdtw",
      tooltip: (
        <Tooltip
          id="pmdtw-tooltip"
          content="Wheelchair type offers manual dynamic tilt required by the user who can not maintain an upright posture through the use of seating components alone. ADP price includes dynamic tilt frame (sizes up to 16&Prime; wide x 16&Prime;deep), standard upholstery, swing away detachable footrests, height adjustable armrests, rear antitippers, brake extensions, 2 point seat belt any closure, front casters, wheels. See Manual Wheelchair Options Chart for other basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "Paediatric Specific Specialty Stroller",
      value: "psss",
      tooltip: (
        <Tooltip
          id="psss-tooltip"
          content="ADP price includes stroller frame and upholstery, foot support, seat and back supports, headrest, chest and pelvic positioning straps (harness), abductor, and wheels. See Manual Wheelchair Options Chart for additional basic components funded by ADP.
        Please refer to the ADP product manual for more details."
          links={[
            {
              id: "link-1",
              text: "ADP Manual",
              href: externalUrls.mobility_devices_product_manual,
            },
          ]}
        />
      ),
    },
    {
      label: "None",
      value: "none",
    },
  ];

  const moveThroughoutOptions = [
    {
      label: "Yes",
      value: "mt-yes",
    },
    { label: "No", value: "mt-no" },
    { label: "N/A", value: "mt-na" },
  ];

  const moveBeyondOptions = [
    {
      label: "Yes",
      value: "mb-yes",
    },
    { label: "No", value: "mb-no" },
    { label: "N/A", value: "mb-na" },
  ];

  const moveThroughoutDependentOptions = [
    {
      label: "Yes",
      value: "mtd-yes",
    },
    { label: "No", value: "mtd-no" },
    { label: "N/A", value: "mtd-na" },
  ];

  const moveBeyondDependentOptions = [
    {
      label: "Yes",
      value: "mbd-yes",
    },
    { label: "No", value: "mbd-no" },
    { label: "N/A", value: "mbd-na" },
  ];

  const moveIndependentlyThroughoutOptions = [
    {
      label: "Yes",
      value: "mit-yes",
    },
    { label: "No", value: "mit-no" },
    { label: "N/A", value: "mit-na" },
  ];

  const moveIndependentlyBeyondOptions = [
    {
      label: "Yes",
      value: "mib-yes",
    },
    { label: "No", value: "mib-no" },
    { label: "N/A", value: "mib-na" },
  ];

  const weightShiftIndependentlyOptions = [
    {
      label: "Yes",
      value: "wsi-yes",
    },
    { label: "No", value: "wsi-no" },
    { label: "N/A", value: "wsi-na" },
  ];

  const historyTissueTraumaOptions = [
    {
      label: "Yes",
      value: "htt-yes",
    },
    { label: "No", value: "htt-no" },
    { label: "N/A", value: "htt-na" },
  ];

  const maintainFunctionalPostureOptions = [
    {
      label: "Yes",
      value: "mfp-yes",
    },
    { label: "No", value: "mfp-no" },
    { label: "N/A", value: "mfp-na" },
  ];

  const intoleranceSittingOptions = [
    {
      label: "Yes",
      value: "is-yes",
    },
    { label: "No", value: "is-no" },
    { label: "N/A", value: "is-na" },
  ];

  const somePowerThroughoutOptions = [
    {
      label: "Yes",
      value: "spt-yes",
    },
    { label: "No", value: "spt-no" },
    { label: "N/A", value: "spt-na" },
  ];

  const somePowerBeyondOptions = [
    {
      label: "Yes",
      value: "spb-yes",
    },
    { label: "No", value: "spb-no" },
    { label: "N/A", value: "spb-na" },
  ];

  const anticipatedOptions = [
    {
      label: "Yes",
      value: "ant-yes",
    },
    { label: "No", value: "ant-no" },
    { label: "N/A", value: "ant-na" },
  ];

  const additionalAdpFundedOptions = [
    {
      label: "Adjustable Tension Back Upholstery",
      value: "2b-atbu",
    },
    {
      label: "Heavy Duty Cross Braces & Upholstery",
      value: "hdcbu",
    },
    {
      label: "Recliner Option",
      value: "recliner-option",
    },
    {
      label: "Angle Adjustable Footplates (pair)",
      value: "angle-adjustable-footplates",
    },
    {
      label: "Elevating Legrests (pair)",
      value: "elevating-legrests",
    },
    {
      label: "Spoke Protectors (pair)",
      value: "spoke-protectors",
    },
    {
      label: "Projected Handrims (pair)",
      value: "projected-handrims",
    },
    {
      label: "Standard Manual Wheelchair Frame with Manual Dynamic Tilt *",
      value: "smwfwmdt",
    },
    {
      label: "Grade Aids (pair)",
      value: "grade-aids",
    },
    {
      label: "Caster Pin Locks (pair)",
      value: "caster-pin-locks",
    },
    {
      label: "Amputee Axle Plates (pair)",
      value: "amputee-axle-plates",
    },
    {
      label: "Quick Release Axles (pair)",
      value: "quick-release-axles",
    },
    {
      label: "Stroller Handles/Paediatric",
      value: "stroller-handles",
    },
    {
      label: "Oxygen Tank Holder",
      value: "2b-oxygen-tank-holder",
    },
    {
      label: "Ventilator Tray",
      value: "2b-ventilator-tray",
    },
    {
      label: "Titanium Frame *",
      value: "titanium-frame",
    },
    {
      label: "Clothing Guards (pair)",
      value: "clothing-guards",
    },
    {
      label: "One Arm/Level Drive",
      value: "one-arm-lever-drive",
    },
    {
      label: "Uni-Lateral Wheel Lock",
      value: "uni-lateral-wheel-lock",
    },
    {
      label: "Plastic Coated Handrims",
      value: "plastic-coated-handrims",
    },
  ];

  // #endregion

  const replacementOptions = [
    {
      label:
        "Change in applicant’s mobility status - previously ADP funded equipment no longer meeting basic mobility needs as defined by ADP for funding purposes",
      value: "2b-change-in-status",
    },
    {
      label:
        "Change in applicant’s body size - previously ADP funded equipment is either too large or too small",
      value: "2b-change-in-size",
    },
    {
      label: "Previously ADP funded equipment is worn out",
      value: "2b-worn-out",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach vendor quote and/or copies of repair bills for wheeled
          walkers and wheelchairs only.
        </TextLink>
      ),
    },
    {
      label: "Special circumstances - none of the above",
      value: "2b-special-circumstances",
      link: (
        <TextLink onClick={() => setUploadModalOpen(true)}>
          - attach letter of rationale.
        </TextLink>
      ),
    },
  ];

  useTriggerOnFieldChange("section2b.reason-for-application", "section2b.replacement-devices");

  const [prescriptionDetailsRequired, setPrescriptionDetailsRequired] =
    useState(false);
  useEffect(() => {
    // LOGIC for conditional requiredText for "Prescription Details"
    if (reasonForApplication2b === "modifications-previous") {
      setPrescriptionDetailsRequired(false);
    } else if (currDevice !== "none") {
      setPrescriptionDetailsRequired(true);
    } else {
      setPrescriptionDetailsRequired(false);
    }
  }, [
    setPrescriptionDetailsRequired,
    currDevice,
    reasonForApplication2b,
  ]);

  // LOGIC for Units Toggle
  const { selectedUnit, toggleAllUnits } = useToggleAllUnits([
    {
      name: "section2b.seat-width-units",
      valuePrefix: "2b-seatWidth-",
      type: "length",
    },
    {
      name: "section2b.seat-depth-units",
      valuePrefix: "2b-seatDepth-",
      type: "length",
    },
    {
      name: "section2b.seat-to-floor-units",
      valuePrefix: "2b-seatToFloor-",
      type: "length",
    },
    {
      name: "section2b.back-cane-height-units",
      valuePrefix: "2b-backCane-",
      type: "length",
    },
    {
      name: "section2b.leg-rest-length-units",
      valuePrefix: "2b-legRest-",
      type: "length",
    },
    {
      name: "section2b.back-height-units",
      valuePrefix: "2b-backHeight-",
      type: "length",
    },
    {
      name: "section2b.client-weight-units",
      valuePrefix: "2b-clientWeight-",
      type: "weight",
    },
  ]);

  // LOGIC for clinical rationale textbox show/hide
  const customModificationsObject = useWatch({
    name: "section2b.custom-modifications-required",
  });
  const checkCustomModifications = customModificationsObject === "cm-yes";

  // LOGIC for popup for additional documents: https://app.asana.com/0/0/1200354578179107/f
  const [prevReplacementDevices, setPrevReplacementDevice] = useState(
    useWatch({ name: "section2b.replacement-devices" })
  );
  const currReplacementDevice = useWatch({
    name: "section2b.replacement-devices",
  });

  const [openPreviouslyFundedModal, setOpenPreviouslyFundedModal] =
    useState(false);
  useEffect(() => {
    if (
      Array.isArray(prevReplacementDevices) &&
      Array.isArray(currReplacementDevice) &&
      (currReplacementDevice?.includes("2b-worn-out") ||
        currReplacementDevice?.includes("2b-special-circumstances")) &&
      !(
        prevReplacementDevices?.includes("2b-worn-out") ||
        prevReplacementDevices?.includes("2b-special-circumstances")
      )
    ) {
      setOpenPreviouslyFundedModal(true);
    }
    setPrevReplacementDevice(currReplacementDevice);
  }, [currReplacementDevice, prevReplacementDevices]);

  // LOGIC for conditional requiredText in replacement-devices
  const currIsReplacementDevicesRequired =
    reasonForApplication2b !== "first-access" &&
    reasonForApplication2b !== "another-type";
  // Need to use useState + useEffect to trigger re-renders
  const [isReplacementDevicesRequired, setIsReplacementDevicesRequired] =
    useState(currIsReplacementDevicesRequired);
  useEffect(() => {
    setIsReplacementDevicesRequired(currIsReplacementDevicesRequired);
  }, [currIsReplacementDevicesRequired]);

  return (
    <>
      <PageMarker page={5} total={13} />
      <CommentGroup
        section="section2b"
        id="1-base-device"
        className={styles.marginAfterPM}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2b"
          name="base-device"
          label="Base Device (check one)"
          options={baseDeviceOptions}
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="base-device-tooltip"
              content="Please refer to the ADP product manual for more details."
              links={[
                {
                  id: "link-1",
                  text: "ADP Manual",
                  href: externalUrls.mobility_devices_product_manual,
                },
              ]}
            />
          }
        />
        <div className={styles.powerAddonCheckbox}>
          <CheckboxGroup
            section="section2b"
            options={[
              {
                label:
                  "Power Add-On Device Requested (check in addition to base device if required)",
                value: "power-addon-requested",
              },
            ]}
            name="power-addon"
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="2-reason"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2b"
          name="reason-for-application"
          label="Reason for Application"
          options={reasonOptions}
          requiredText="This is a required field."
          readOnly={mode !== "edit"}
        />
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="3-replacement-devices"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2b"
          options={replacementOptions}
          name="replacement-devices"
          label="Replacement Device(s) and/or Modifications Required Due To: (check as appropriate)"
          requiredText={
            isReplacementDevicesRequired
              ? "This is a required field."
              : undefined
          }
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="replacement-devices-tooltip"
              content="If you are selecting 'Previously funded ADP device is worn out', please provide a repair quote (Unless only seating needs to be replaced, then a quote is not required)."
            />
          }
        />
        <Modal
          title="Supporting Documents"
          isOpen={isUploadModalOpen}
          onClose={() => {
            setUploadModalOpen(false);
          }}
        >
          <UploadDocumentsModal
            clientFileId={clientFile.id}
            setUploadModalOpen={setUploadModalOpen}
          />
        </Modal>
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="4-confirmation"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text component="span" size="large" weight="bold">
          Confirmation of Applicant’s Eligibility for Manual Wheelchair: (answer
          required for each statement)
        </Text>
        <div className={styles.numberedRadioGroups}>
          <Text className={styles.numberBullet} weight="bold">
            1.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-throughout-residence"
            description="Applicant requires the use of a manual wheelchair to move throughout their place of residence
          and can move independently throughout their place of residence with the prescribed device."
            options={moveThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            2.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-beyond-residence"
            description="Applicant requires the use of a manual wheelchair to move beyond their place of residence
          and can move independently beyond their place of residence with the prescribed device."
            options={moveBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            3.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-throughout-dependent"
            description="Applicant requires the use of a manual wheelchair to move throughout their place of
          residence and is dependent on attendant for propulsion."
            options={moveThroughoutDependentOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            4.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-beyond-dependent"
            description="Applicant requires the use of a manual wheelchair to move beyond their place of residence
          and is dependent on attendant for propulsion."
            options={moveBeyondDependentOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            5.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-independently-throughout"
            description="Applicant requires the use of a titanium frame wheelchair to move independently throughout
          their place of residence."
            options={moveIndependentlyThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            6.
          </Text>
          <RadioGroup
            section="section2b"
            name="move-independently-beyond"
            description="Applicant requires the use of a titanium frame wheelchair to move independently beyond their place of residence."
            options={moveIndependentlyBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            7.
          </Text>
          <RadioGroup
            section="section2b"
            name="weight-shift-independently"
            description="Applicant can weight shift independently in the sitting position."
            options={weightShiftIndependentlyOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            8.
          </Text>
          <RadioGroup
            section="section2b"
            name="history-tissue-trauma"
            description="Applicant demonstrates a history of tissue trauma and/or a significant risk of tissue trauma
          when sitting and skin integrity cannot be maintained with the addition of fixed seating alone."
            options={historyTissueTraumaOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            9.
          </Text>
          <RadioGroup
            section="section2b"
            name="maintain-functional-posture"
            description="Applicant cannot maintain a functional posture in sitting due to abnormal tone and/or joint
          contractures and posture cannot be supported with the addition of fixed seating alone."
            options={maintainFunctionalPostureOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            10.
          </Text>
          <RadioGroup
            section="section2b"
            name="intolerance-sitting"
            description="Applicant demonstrates an intolerance for sitting which cannot be increased for mobility with
          the addition of fixed seating alone."
            options={intoleranceSittingOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            11.
          </Text>
          <RadioGroup
            section="section2b"
            name="some-power-throughput"
            description="Applicant is able to propel a manual wheelchair independently but requires some daily use of
          power to move throughout their place of residence."
            options={somePowerThroughoutOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            12.
          </Text>
          <RadioGroup
            section="section2b"
            name="some-power-beyond"
            description="Applicant is able to propel a manual wheelchair independently but requires some daily use of
          power to move beyond their place of residence."
            options={somePowerBeyondOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
          <Text className={styles.numberBullet} weight="bold">
            13.
          </Text>
          <RadioGroup
            section="section2b"
            name="anticipated"
            description="It is anticipated that the applicant will be able to use a manual wheelchair with a power add-on
          device for their long-term mobility needs and will not require the use of a power wheelchair/
          power base within the designated funding period."
            options={anticipatedOptions}
            horizontal
            requiredText="This field is required."
            readOnly={mode !== "edit"}
          />
        </div>
      </CommentGroup>
      <PageMarker page={6} total={13} />
      <CommentGroup
        section="section2b"
        id="5-prescription-details"
        className={classNames(styles.marginAfterPM, styles.childrenSpacing)}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <Text size="large" weight="bold" className={styles.tooltipText}>
          Prescription Details for Manual Wheelchair Only (answers required for
          all specifications){" "}
          <Tooltip
            id="prescription-details-tooltip"
            content="See product manual for details about all generic device types"
            links={[
              {
                id: "link-1",
                text: "Product Manual",
                href: externalUrls.mobility_devices_product_manual,
              },
            ]}
          />
        </Text>
        <Text size="large">Toggle All Units</Text>

        <ToggleAllUnitsButton
          section="section2b"
          setMetric={() => {
            toggleAllUnits("metric");
          }}
          setImperial={() => {
            toggleAllUnits("imperial");
          }}
          selectedUnit={selectedUnit}
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="seat-width"
          label="Seat Width"
          type="length"
          valuePrefix="2b-seatWidth"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="seat-width-tooltip"
                content="If seat width is greater than 18&Prime;, additional funding is available under funding code WAMA. "
              />
            )
          }
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="seat-depth"
          label="Seat Depth"
          type="length"
          valuePrefix="2b-seatDepth"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="seat-depth-tooltip"
                content="If seat depth is greater than 18&Prime;, additional funding is available under funding code WAMB."
              />
            )
          }
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="seat-to-floor"
          label="Finished Seat to Floor Height"
          type="length"
          valuePrefix="2b-seatToFloor"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="back-cane-height"
          label="Back Cane Height"
          type="length"
          valuePrefix="2b-backCane"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="back-height"
          label="Finished Back Height"
          type="length"
          valuePrefix="2b-backHeight"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          formSection="section2b"
          formFieldId="leg-rest-length"
          label="Finished Leg Rest Length"
          type="length"
          valuePrefix="2b-legRest"
          readOnly={mode !== "edit"}
        />
        <MeasurementInput
          required={prescriptionDetailsRequired}
          formSection="section2b"
          formFieldId="client-weight"
          label="Client Weight"
          type="weight"
          valuePrefix="2b-clientWeight"
          readOnly={mode !== "edit"}
          tooltip={
            isSalesRep && (
              <Tooltip
                id="client-weight-tooltip"
                content="Additional funding is available under funding codes WAMF for 250-350 lbs, Code WAMG for 350-400 lbs, and Code WAMH for 400 lbs plus "
              />
            )
          }
        />
      </CommentGroup>

      <CommentGroup
        section="section2b"
        id="6-additional"
        className={styles.margin}
        customPosition={styles.moveCommentDown} // avoid overlap with tooltip
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <CheckboxGroup
          section="section2b"
          options={additionalAdpFundedOptions}
          name="additional-adp-funded-options"
          label="Additional ADP Funded Options Required for Prescribed Manual Wheelchair: (check one or more)"
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="additional-adp-funded-tooltip"
              content={
                isSalesRep
                  ? "If the sales quote includes the ADP code WANA, Standard Manual Wheelchair Frame with Manual Dynamic Tilt should be checked. If standard manual wheelchair frame with manual dynamic tilt or titanium frame is selected, please provide the clinical rationale for that. You can also refer to the ADP Manual for more information. "
                  : "If standard manual wheelchair frame with manual dynamic tilt or titanium frame is selected, please provide the clinical rationale for that. You can also refer to the ADP Manual for more information."
              }
              links={[
                {
                  id: "link-1",
                  text: "Mobility Devices Manual",
                  href: externalUrls.mobility_devices_policy_and_administration_manual,
                },
              ]}
            />
          }
        />
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="7-clinical-rationale"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextArea
          section="section2b"
          id="provide-clinical-rationale"
          label="* Provide Clinical Rationale"
          description="If standard manual wheelchair frame with manual dynamic tilt or titanium frame is selected, please provide the clinical rationale for that."
          placeholder="Provide clinical rationale for standard manual wheelchair frame with manual dynamic tilt or titanium frame."
          maxLength={480}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="8-non-adp-funded"
        className={styles.margin}
        readOnly={mode === "locked"}
      >
        <TextGrid
          section="section2b"
          id="non-adp-funded-options-prescribed"
          label="Non ADP Funded Options Prescribed"
          maxInputs={9}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="9-setup-instructions"
        className={styles.margin}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <TextGrid
          section="section2b"
          id="setup-instructions-for-vendor"
          label="Set Up Instructions for Vendor"
          maxInputs={18}
          maxLength={35}
          readOnly={mode !== "edit"}
          fieldOptions={nonSpecialCharacterOptions}
        />
      </CommentGroup>
      <CommentGroup
        section="section2b"
        id="10-custom-modifications"
        className={styles.margin}
        commentBoxPosition={!checkCustomModifications ? "top" : undefined}
        locked={mode === "locked"}
        readOnly={clientFile.status === "PendingSignature"}
      >
        <RadioGroup
          section="section2b"
          name="custom-modifications-required"
          label="Custom Modifications Required"
          options={customModificationsOptions}
          horizontal
          requiredText="This field is required."
          readOnly={mode !== "edit"}
          tooltip={
            <Tooltip
              id="custom-modifications-tooltip"
              content="If custom modifications are made on a device, a custom modification quote is required that is specifically associated with the custom work. Please include the cost for parts and labour, as well as, clinical rationale that led to this custom modification being required. "
            />
          }
        />
        {checkCustomModifications && (
          <TextArea
            section="section2b"
            id="custom-modifications-clinical-rationale"
            label="Custom Modifications Clinical Rationale"
            description="The authorizer must provide clinical rationale to support the request in the space below and attach a vendor quote that provides
          a breakdown of the cost of labour (not to exceed $40.00/hour) and parts."
            placeholder="Custom modifications required - clinical rationale"
            maxLength={480}
            className={styles.paddingNoHorizontal}
            readOnly={mode !== "edit"}
            fieldOptions={nonSpecialCharacterOptions}
          />
        )}
      </CommentGroup>
      {fileStatus !== "Completed" && (
        <Modal
          isOpen={openPreviouslyFundedModal}
          title="Coverage for Mobility Devices"
          onClose={() => setOpenPreviouslyFundedModal(false)}
        >
          <div className={styles.modalText}>
            Please provide a repair quote (Unless only seating needs to be
            replaced, then a quote is not required).
          </div>
          <div className={styles.horizontalLine} />
          <div className={styles.modalButton}>
            <Button primary onClick={() => setOpenPreviouslyFundedModal(false)}>
              Close and return to form
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default Section2b;
